import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Paper, Typography, Button, Box, CircularProgress, withStyles,
    RadioGroup, FormControlLabel, Radio
} from "@material-ui/core";
import { getAiGradingPresets, getAiGradingSettings, saveAiGradingSettings } from "./aiGradingSettingsActions";

const styles = theme => ({
    root: { padding: theme.spacing(3), margin: theme.spacing(2) },
    title: { fontWeight: 'bold' },
    help: { color: theme.palette.text.secondary, marginBottom: theme.spacing(2) },
    option: {
        alignItems: 'flex-start',
        marginBottom: theme.spacing(1),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 6,
        padding: theme.spacing(1),
        width: '100%',
        marginLeft: 0,
    },
    optionSelected: { borderColor: theme.palette.primary.main, backgroundColor: theme.palette.action.hover },
    optionName: { fontWeight: 600 },
    optionDesc: { color: theme.palette.text.secondary, fontSize: 13 },
    actions: { marginTop: theme.spacing(2) },
});

const AiGradingSettings = ({ classes }) => {
    const dispatch = useDispatch();
    const activeCustomer = useSelector(state => state?.core?.login?.activeCustomer?.CustomerID ?? null);
    const translations = useSelector(state => state?.core?.translation?.translations ?? {});

    const [presets, setPresets] = useState([]);
    const [selected, setSelected] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        let mounted = true;
        const load = async () => {
            if (!activeCustomer) { setLoading(false); return; }
            setLoading(true);
            const [list, settings] = await Promise.all([
                dispatch(getAiGradingPresets({ ActiveCustomerID: activeCustomer })),
                dispatch(getAiGradingSettings({ ActiveCustomerID: activeCustomer })),
            ]);
            if (mounted) {
                setPresets(list || []);
                setSelected((settings && settings.PresetKey) || (list && list[0] && list[0].key) || '');
                setLoading(false);
            }
        };
        load();
        return () => { mounted = false; };
    }, [activeCustomer, dispatch]);

    const onSave = async () => {
        if (!selected) return;
        setSaving(true);
        await dispatch(saveAiGradingSettings({ ActiveCustomerID: activeCustomer, PresetKey: selected }));
        setSaving(false);
    };

    return (
        <Paper className={classes.root} elevation={2}>
            <Typography variant="h6" className={classes.title}>
                {translations['ai_grading_profile'] || 'AI Grading Profile'}
            </Typography>
            <Typography variant="body2" className={classes.help}>
                {translations['ai_grading_profile_help'] ||
                    'Choose how the AI should grade your devices. Securaze maintains these profiles — pick the one that matches your channel; the AI tuning is handled for you.'}
            </Typography>

            {loading ? (
                <Box display="flex" alignItems="center"><CircularProgress size={18} /><span style={{ marginLeft: 8 }}>Loading…</span></Box>
            ) : (
                <>
                    <RadioGroup value={selected} onChange={(e) => setSelected(e.target.value)}>
                        {presets.map((p) => (
                            <FormControlLabel
                                key={p.key}
                                value={p.key}
                                control={<Radio color="primary" />}
                                className={`${classes.option} ${selected === p.key ? classes.optionSelected : ''}`}
                                label={
                                    <span>
                                        <Typography className={classes.optionName}>{p.name}</Typography>
                                        <Typography className={classes.optionDesc}>{p.description}</Typography>
                                    </span>
                                }
                            />
                        ))}
                    </RadioGroup>
                    <div className={classes.actions}>
                        <Button variant="contained" color="primary" onClick={onSave} disabled={saving || !selected || !activeCustomer}>
                            {saving ? (translations['saving'] || 'Saving…') : (translations['save'] || 'Save')}
                        </Button>
                    </div>
                </>
            )}
        </Paper>
    );
};

export default withStyles(styles)(AiGradingSettings);
